"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { ArrowRight, BookOpen, Clock, Calendar, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
interface FallbackArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  image: string;
  imageSrcset: string;
  imageSizes: string;
}
const fallbackArticles: FallbackArticle[] = [{
  id: "guide-1",
  title: "Kitchen remodel costs in Decatur: 2025 budget guide",
  excerpt: "Understand realistic cabinetry, layout reconfiguration, and finish allowances for historic bungalows and mid-century homes.",
  category: "Kitchens",
  readTime: "6 min read",
  date: "Oct 14, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_remodel_decatur_guide.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_remodel_decatur_guide.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/kitchen_remodel_decatur_guide.png-wvc-sizes"
}, {
  id: "guide-2",
  title: "Master bathroom planning: layout vs. luxury upgrades",
  excerpt: "Where to allocate your budget between wet-room reconfigurations, custom vanities, and water heating upgrades.",
  category: "Bathrooms",
  readTime: "5 min read",
  date: "Sep 28, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/master_bathroom_budget_planning.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/master_bathroom_budget_planning.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/master_bathroom_budget_planning.png-wvc-sizes"
}, {
  id: "guide-3",
  title: "Navigating historic home renovation timelines in Oakhurst",
  excerpt: "Permitting nuances, structural preservation steps, and how we keep heritage updates on schedule.",
  category: "Whole-Home",
  readTime: "8 min read",
  date: "Sep 12, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/historic_home_renovation_timeline.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/historic_home_renovation_timeline.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/historic_home_renovation_timeline.png-wvc-sizes"
}, {
  id: "guide-4",
  title: "Remodeling vs. moving: what makes sense in Decatur",
  excerpt: "Evaluating neighborhood equity, property tax implications, and customization advantages before listing your home.",
  category: "Planning",
  readTime: "7 min read",
  date: "Aug 30, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/remodeling_vs_moving_decatur.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/remodeling_vs_moving_decatur.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/remodeling_vs_moving_decatur.png-wvc-sizes"
}, {
  id: "guide-5",
  title: "Second-story addition feasibility for cottage foundations",
  excerpt: "Structural load calculations, roofline tie-ins, and living arrangements during vertical home expansions.",
  category: "Additions",
  readTime: "9 min read",
  date: "Aug 15, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/second_story_addition_feasibility.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/second_story_addition_feasibility.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/second_story_addition_feasibility.png-wvc-sizes"
}, {
  id: "guide-6",
  title: "Basement conversion guidelines for humid Georgia climates",
  excerpt: "Sub-slab waterproofing, HVAC zoning, and egress compliance for livable guest suites and media rooms.",
  category: "Basements",
  readTime: "6 min read",
  date: "Jul 22, 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/basement_conversion_guidelines.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/basement_conversion_guidelines.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/basement_conversion_guidelines.png-wvc-sizes"
}];
function ArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        {[0, 1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-xl border border-border bg-card/60 p-4 space-y-4 animate-pulse">
            <div className="h-52 w-full rounded-lg bg-muted" />
            <div className="flex gap-2">
              <div className="h-5 w-16 rounded bg-muted" />
              <div className="h-5 w-24 rounded bg-muted" />
            </div>
            <div className="h-7 w-4/5 rounded bg-muted" />
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-5 w-28 rounded bg-muted pt-2" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-destructive/30 bg-card p-8 text-center max-w-xl mx-auto">
        <AlertCircle className="size-8 mx-auto text-destructive mb-3" />
        <p className="text-foreground font-medium mb-1">Unable to load journal entries</p>
        <p className="text-sm text-muted-foreground mb-4">
          There was a temporary problem fetching our remodeling guides.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry loading
        </Button>
      </div>;
  }
  const hasPosts = posts && posts.length > 0;
  return <div className="space-y-12">
      {hasPosts ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Renovation Guide";
        const postDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric"
        }) : "Recent";
        return <article key={post.id} data-index={i} className="group flex flex-col">
                <Card className="flex flex-col h-full overflow-hidden border border-border bg-card/90 backdrop-blur-md rounded-xl transition-[color,background-color,border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                  <div className="relative overflow-hidden bg-muted">
                    <AspectRatio ratio={16 / 10}>
                      {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary text-muted-foreground">
                          <BookOpen className="size-10 stroke-[1.5]" />
                        </div>}
                    </AspectRatio>
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-background/95 text-foreground border border-border/80 shadow-xs font-semibold text-xs tracking-wider uppercase backdrop-blur-xs">
                        {categoryName}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="flex flex-col flex-1 p-6 sm:p-7 justify-between gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                          {postDate}
                        </span>
                        <span>•</span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="size-3.5" />
                          5 min read
                        </span>
                      </div>

                      <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-snug text-foreground group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>

                      {post.excerpt && <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed">
                          {post.excerpt.replace(/<[^>]*>?/gm, "")}
                        </p>}
                    </div>

                    <div className="pt-2 border-t border-border/60 flex items-center justify-between">
                      <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                        <span>Read full guide</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </article>;
      })}
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="group flex flex-col">
              <Card className="flex flex-col h-full overflow-hidden border border-border bg-card/90 backdrop-blur-md rounded-xl transition-[color,background-color,border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={16 / 10}>
                    <img src={article.image} data-wvc-srcset={article.imageSrcset} data-wvc-sizes={article.imageSizes} alt={article.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </AspectRatio>
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-background/95 text-foreground border border-border/80 shadow-xs font-semibold text-xs tracking-wider uppercase backdrop-blur-xs">
                      {article.category}
                    </Badge>
                  </div>
                </div>

                <CardContent className="flex flex-col flex-1 p-6 sm:p-7 justify-between gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                        {article.date}
                      </span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="size-3.5" />
                        {article.readTime}
                      </span>
                    </div>

                    <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-snug text-foreground group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>

                    <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-border/60 flex items-center justify-between">
                    <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      <span>Read full guide</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </article>)}
        </div>}

      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-6">
          <Button variant="outline" size="default" onClick={prevPage} disabled={!hasPrev} className="border-border text-foreground hover:bg-secondary">
            Previous
          </Button>
          <Button variant="default" size="default" onClick={nextPage} disabled={!hasNext} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Next
          </Button>
        </div>}
    </div>;
}
export default function Articles() {
  const queryParams = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wpQuery = new WP_Query(queryParams);
  return <section id="articles" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center mb-4">
            <span className="text-xs sm:text-sm font-bold tracking-widest text-primary uppercase bg-secondary px-3.5 py-1 rounded-full border border-border">
              Architectural Journal
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight">
            Remodeling guides for Decatur homes
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Practical cost breakdowns, historic home timelines, and planning advice from local builders.
          </p>

          <div className="mt-6 flex items-center justify-center gap-2">
            <span className="h-0.5 w-12 bg-primary rounded-full" />
            <span className="h-0.5 w-2 bg-border rounded-full" />
            <span className="h-0.5 w-2 bg-border rounded-full" />
          </div>
        </header>

        <PostsProvider className="articles-posts-wrapper" wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesConsumer />
        </PostsProvider>

        <footer className="mt-16 sm:mt-20 pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 bg-card/60 backdrop-blur-md rounded-xl p-6 sm:p-8 border">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-serif text-lg font-semibold text-foreground">
              Planning a renovation in your neighborhood?
            </h3>
            <p className="text-sm text-muted-foreground">
              Schedule an on-site consultation to review scope, historic zoning, and structural feasibility.
            </p>
          </div>
          <Link to="/ContactPage">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shrink-0">
              Request a consultation
            </Button>
          </Link>
        </footer>
      </div>
    </section>;
}